
import React, { useState, useMemo } from 'react';
import { MutualHazard, Contact } from '../types';
import { Shuffle, AlertTriangle, ShieldCheck, Info, X, Save, Users, List, Grid3X3, ArrowRight, Sparkles, Loader2, Zap, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { predictMutualHazards } from '../services/geminiService';
import {
  leseKonfliktVorhersage,
  vorhersageFehlerText,
  vorschlaegeZeile,
  vorschlagUebernehmen,
  type KonfliktVorschlag,
} from './hazardMatrixLogik';
import type { KoordinierungPaar } from './koordinierung/koordinierungLogic';
import {
  KONFLIKT_STANDARD,
  schlageZuordnungVor,
  uebernahmeKandidaten,
  uebernehmeInMatrix,
  vorbelegung,
  zeitangabenFuerKi,
  type Konfliktloesung,
  type Konfliktwahl,
  type Zuordnung,
} from './koordinierung/uebernahmeMatrix';

interface Props {
  hazards: MutualHazard[];
  contacts: Contact[];
  onChange: (hazards: MutualHazard[]) => void;
  /**
   * Bewertete Gewerkepaare aus dem Modul „Gewerke-Überschneidung".
   *
   * OPTIONAL, und das ist der Kern: ohne diese Prop ist die Ansicht Zeichen
   * für Zeichen die von heute. Diese Matrix ist der einzige Weg für Projekte
   * OHNE SiGe-Plan (Begehungs- und HSE-Projekte) und bleibt deshalb für sich
   * allein vollständig.
   */
  koordinierungPaare?: KoordinierungPaar[];
  /**
   * Meldet die übernommenen Paar-Ids zurück. Der Aufrufer setzt daraufhin
   * `koordinierung_paare.uebernommen_matrix_am` — ohne diesen Vermerk
   * wiederholte sich die Übernahme stillschweigend und überschriebe von Hand
   * nachgebesserte Zellen.
   */
  onKoordinierungUebernommen?: (paarIds: string[]) => void;
}

export const HazardMatrixView: React.FC<Props> = ({
  hazards, contacts, onChange, koordinierungPaare, onKoordinierungUebernommen,
}) => {
  const [editingHazard, setEditingHazard] = useState<{ source: string; affected: string } | null>(null);
  const [viewMode, setViewMode] = useState<'matrix' | 'list' | 'ai'>('matrix');
  const [formData, setFormData] = useState<{ description: string; measure: string; severity: 'low' | 'medium' | 'high' }>({
    description: '', measure: '', severity: 'medium'
  });
  
  const [aiSuggestions, setAiSuggestions] = useState<KonfliktVorschlag[]>([]);
  const [isAiLoading, setIsAiLoading] = useState(false);
  /** Meldung im KI-Kasten: Aufruf gescheitert oder nichts Übernehmbares geliefert. */
  const [aiMeldung, setAiMeldung] = useState<string | null>(null);
  /** Zusatz zur Vorschlagsliste: unvollständig, Vorschläge ohne Gewerk oder nicht lesbar. */
  const [aiHinweis, setAiHinweis] = useState<string | null>(null);
  const [selectedTrade, setSelectedTrade] = useState<string | null>(null);

  const trades = useMemo(() => {
    const set = new Set<string>();
    contacts.forEach(c => {
      const name = c.company || c.role || 'Unbekannt';
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [contacts]);

  // ─── Rückspeisung aus der Koordinierung ──────────────────────────────────
  // Nichts hiervon läuft von selbst: die Kandidaten werden nur GEZÄHLT, die
  // Übernahme geschieht ausschließlich im Dialog auf Knopfdruck.
  const [uebernahmeOffen, setUebernahmeOffen] = useState(false);
  const [zuordnung, setZuordnung] = useState<Zuordnung>({});
  /**
   * Was mit Zellen geschieht, in denen schon etwas steht — je Zelle einzeln.
   * Ein fehlender Eintrag heißt `KONFLIKT_STANDARD` (zusammenführen): der
   * verlustfreie Weg ist der Standard, nicht die Ausnahme.
   */
  const [konfliktwahl, setKonfliktwahl] = useState<Konfliktwahl>({});

  const kandidaten = useMemo(() => uebernahmeKandidaten(koordinierungPaare), [koordinierungPaare]);

  /** Die Achsen dieser Matrix, samt Tätigkeit — die Namensbrücke zum Plan. */
  const achsen = useMemo(
    () => trades.map(t => {
      const contact = contacts.find(c => (c.company || c.role || 'Unbekannt') === t);
      return { name: t, taetigkeit: contact?.tradeType || contact?.role || null };
    }),
    [trades, contacts],
  );

  const vorschlaege = useMemo(
    () => schlageZuordnungVor(koordinierungPaare, achsen),
    [koordinierungPaare, achsen],
  );

  /** Vorschau — dieselbe reine Funktion, die auch die Übernahme ausführt. */
  const vorschau = useMemo(
    () => uebernehmeInMatrix(hazards, koordinierungPaare, zuordnung, konfliktwahl),
    [hazards, koordinierungPaare, zuordnung, konfliktwahl],
  );

  const oeffneUebernahme = () => {
    // Vorbelegt wird NUR, was sich eindeutig deckt. Alles andere bleibt leer
    // und muss ausgewählt werden — ein geratener Name stünde sonst als zweite
    // Zeile für dieselbe Firma in der Matrix.
    setZuordnung(vorbelegung(vorschlaege));
    // Die Konfliktwahl beginnt bei jedem Öffnen leer: eine Entscheidung von
    // vorhin galt einem anderen Stand der Matrix.
    setKonfliktwahl({});
    setUebernahmeOffen(true);
  };

  const uebernehmen = () => {
    if (vorschau.uebernommenePaarIds.length === 0) return;
    onChange(vorschau.hazards);
    onKoordinierungUebernommen?.(vorschau.uebernommenePaarIds);
    setUebernahmeOffen(false);
  };

  const fetchAiSuggestions = async () => {
    // Zeitangaben aus der Koordinierung, soweit die Namen zugeordnet sind.
    // Sie sind ein Hinweis für die KI und ändern KEINE Daten — ohne sie rät
    // die KI über die Gleichzeitigkeit, mit ihr weiß sie es.
    const zeit = zeitangabenFuerKi(koordinierungPaare, { ...vorbelegung(vorschlaege), ...zuordnung });

    // Build structured data for AI: Name (ID for Matrix) + Activity (TradeType for Logic)
    const tradesData = trades.map(t => {
        const contact = contacts.find(c => (c.company || c.role || 'Unbekannt') === t);
        const gleichzeitigMit = zeit[t];
        return {
            name: t,
            activity: contact?.tradeType || contact?.role || t, // Fallback logic
            ...(gleichzeitigMit?.length ? { gleichzeitigMit } : {}),
        };
    });

    if (tradesData.length < 2) return;
    
    setIsAiLoading(true);
    setAiMeldung(null);
    try {
      // Gelesen, nicht durchgereicht: der Proxy liefert { hazards: [{ gewerkA, … }] },
      // die Liste unten braucht Zellen der Matrix (hazardMatrixLogik.ts).
      const vorhersage = leseKonfliktVorhersage(await predictMutualHazards(tradesData), trades);
      if (vorhersage.vorschlaege.length === 0) {
        // Vorschläge eines früheren Laufs bleiben stehen.
        setAiMeldung(vorhersage.meldung);
        return;
      }
      setAiSuggestions(vorhersage.vorschlaege);
      setAiHinweis(vorhersage.hinweis);
      setViewMode('ai');
    } catch (e) {
      console.error('Konflikte vorhersagen fehlgeschlagen', e);
      setAiMeldung(vorhersageFehlerText(navigator.onLine));
    } finally {
      setIsAiLoading(false);
    }
  };

  const getHazard = (source: string, affected: string) => {
    return hazards.find(h => h.tradeSource === source && h.tradeAffected === affected);
  };

  const handleCellClick = (source: string, affected: string) => {
    if (source === affected) return;
    const existing = getHazard(source, affected);
    setEditingHazard({ source, affected });
    setFormData({
      description: existing?.description || '',
      measure: existing?.measure || '',
      severity: existing?.severity || 'medium'
    });
  };

  const saveHazard = (data = formData, pair = editingHazard) => {
    if (!pair) return;
    const { source, affected } = pair;
    
    let newHazards = hazards.filter(h => !(h.tradeSource === source && h.tradeAffected === affected));
    
    if (data.description.trim() || data.measure.trim()) {
      newHazards.push({
        id: crypto.randomUUID(),
        tradeSource: source,
        tradeAffected: affected,
        ...data
      });
    }
    
    onChange(newHazards);
    setEditingHazard(null);
  };

  const applyAiSuggestion = (s: KonfliktVorschlag) => {
    // Belegte Zelle: zusammenführen statt einer zweiten Zeile, die der
    // Zellen-Editor nie zeigt und beim Speichern löscht (hazardMatrixLogik.ts).
    onChange(vorschlagUebernehmen(hazards, s, () => crypto.randomUUID()));
    setAiSuggestions(prev => prev.filter(item => !(item.tradeSource === s.tradeSource && item.tradeAffected === s.tradeAffected)));
  };

  const removeHazard = () => {
    if (!editingHazard) return;
    const { source, affected } = editingHazard;
    onChange(hazards.filter(h => !(h.tradeSource === source && h.tradeAffected === affected)));
    setEditingHazard(null);
  };

  const RadarDiagram = () => {
    if (trades.length < 2) return (
        <div className="h-[500px] flex items-center justify-center bg-white rounded-2xl border border-slate-200 text-slate-500 text-center p-8">
            <Users className="w-12 h-12 mb-4 mx-auto opacity-20" />
            <p className="text-sm">Mindestens zwei Gewerke in den Kontakten nötig für das Radar.</p>
        </div>
    );
    
    // Increased view box size significantly to prevent text clipping
    const size = 800;
    const center = size / 2;
    const radius = 190; // Increased to 190 for larger appearance
    
    const points = trades.map((t, i) => {
      const angle = (i / trades.length) * Math.PI * 2 - Math.PI / 2;
      return {
        name: t,
        x: center + radius * Math.cos(angle),
        y: center + radius * Math.sin(angle),
        angle
      };
    });

    // Helper to split text into multiple lines if too long
    const splitText = (text: string, maxChars = 15): string[] => {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            if (currentLine.length + 1 + words[i].length <= maxChars) {
                currentLine += ' ' + words[i];
            } else {
                lines.push(currentLine);
                currentLine = words[i];
            }
        }
        lines.push(currentLine);
        return lines;
    };

    return (
      <div className="bg-white rounded-2xl p-2 flex flex-col items-center justify-center relative shadow-sm overflow-hidden border border-slate-200 h-[500px]">
        <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
            <Zap className="text-blue-600 w-4 h-4 animate-pulse" />
            <span className="text-blue-800 font-bold text-[11px] tracking-widest uppercase">Schnittstellen-Radar</span>
        </div>
        
        {/* Legend */}
        <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-xl border border-slate-100 shadow-sm text-[11px] flex flex-col gap-1.5 z-10">
            <div className="font-bold text-slate-400 uppercase tracking-wider mb-0.5">Legende</div>
            <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-red-500 h-1 rounded-full"></div>
                <span className="text-slate-600 font-medium">Hoch</span>
            </div>
            <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-yellow-400 h-1 rounded-full"></div>
                <span className="text-slate-600 font-medium">Mittel</span>
            </div>
            <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 border-t-2 border-blue-500 border-dashed"></div>
                <span className="text-slate-600 font-medium">Gering</span>
            </div>
        </div>
        
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full max-w-full">
          <defs>
              <filter id="glow">
                  <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                  <feMerge>
                      <feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/>
                  </feMerge>
              </filter>
              <radialGradient id="radarGradient" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="#f8fafc" />
                  <stop offset="100%" stopColor="#f1f5f9" />
              </radialGradient>
          </defs>
          
          {/* Background Concentric Circles - More Visible */}
          <circle cx={center} cy={center} r={radius} fill="url(#radarGradient)" stroke="#cbd5e1" strokeWidth="2" />
          <circle cx={center} cy={center} r={radius * 0.75} fill="none" stroke="#e2e8f0" strokeWidth="1.5" strokeDasharray="4 4" />
          <circle cx={center} cy={center} r={radius * 0.5} fill="none" stroke="#e2e8f0" strokeWidth="1.5" strokeDasharray="4 4" />
          <circle cx={center} cy={center} r={radius * 0.25} fill="none" stroke="#e2e8f0" strokeWidth="1.5" strokeDasharray="4 4" />
          
          {/* Axis lines from center to nodes */}
          {points.map((p, i) => (
             <line key={`axis-${i}`} x1={center} y1={center} x2={p.x} y2={p.y} stroke="#f1f5f9" strokeWidth="1" />
          ))}

          {hazards.map(h => {
            const p1 = points.find(p => p.name === h.tradeSource);
            const p2 = points.find(p => p.name === h.tradeAffected);
            if (!p1 || !p2) return null;
            const isRelated = selectedTrade ? (h.tradeSource === selectedTrade || h.tradeAffected === selectedTrade) : true;
            
            return (
              <line 
                key={h.id} 
                x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} 
                stroke={h.severity === 'high' ? '#ef4444' : h.severity === 'medium' ? '#fbbf24' : '#3b82f6'} 
                strokeWidth={h.severity === 'high' ? "3" : "2"}
                strokeDasharray={h.severity === 'low' ? "6 4" : "0"}
                className={`transition-all duration-500 ${isRelated ? 'opacity-90' : 'opacity-10'}`}
                filter={h.severity === 'high' ? "url(#glow)" : ""}
              />
            );
          })}

          {points.map(p => {
             const isSelected = selectedTrade === p.name;
             const isRelated = selectedTrade 
                ? hazards.some(h => (h.tradeSource === selectedTrade && h.tradeAffected === p.name) || (h.tradeAffected === selectedTrade && h.tradeSource === p.name))
                : false;
             
             // Calculate text anchor and offset based on angle
             const cosAngle = Math.cos(p.angle);
             const sinAngle = Math.sin(p.angle);
             
             // Push text further out
             const textOffset = 25; 
             const textX = p.x + (cosAngle * textOffset);
             const textY = p.y + (sinAngle * textOffset);
             
             // Improved anchor logic for better text alignment
             const anchor = Math.abs(cosAngle) < 0.1 ? 'middle' : (cosAngle > 0 ? 'start' : 'end');
             
             // Split text for multiline
             const lines = splitText(p.name, 20);

             return (
                <g 
                    key={p.name} 
                    className="group cursor-pointer"
                    onMouseEnter={() => setSelectedTrade(p.name)}
                    onMouseLeave={() => setSelectedTrade(null)}
                >
                  <circle 
                    cx={p.x} cy={p.y} r={isSelected ? "8" : "5"} 
                    fill={isSelected || isRelated ? "#3b82f6" : "#ffffff"} 
                    stroke="#3b82f6" 
                    strokeWidth={isSelected ? "3" : "2"} 
                    className="transition-all duration-300 drop-shadow-sm" 
                  />
                  {/* Highlight ring on hover */}
                  {isSelected && (
                     <circle cx={p.x} cy={p.y} r="14" fill="none" stroke="#3b82f6" strokeWidth="1" opacity="0.3" className="animate-ping-slow" />
                  )}

                  <text 
                    x={textX} 
                    y={textY} 
                    fill={isSelected ? "#0f172a" : (selectedTrade && !isRelated ? "#cbd5e1" : "#475569")} 
                    fontSize={isSelected ? "13" : "11"} 
                    fontWeight={isSelected ? "bold" : "500"}
                    textAnchor={anchor}
                    className="pointer-events-none transition-all select-none"
                    dominantBaseline="middle"
                  >
                     {lines.map((line, li) => (
                        <tspan 
                            key={li} 
                            x={textX} 
                            dy={li === 0 ? (lines.length > 1 ? "-0.5em" : "0.3em") : "1.2em"}
                        >
                            {line}
                        </tspan>
                     ))}
                  </text>
                </g>
             );
          })}
        </svg>
      </div>
    );
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto animate-fade-in pb-20">

      {/* Hinweisband: bewertete Paare aus der Koordinierung.

          Die Bedingung nennt `onKoordinierungUebernommen` AUSDRÜCKLICH und
          verlässt sich nicht darauf, dass ohne Rückmeldeweg auch keine Paare
          geladen werden: kippt `isReadOnly` erst nach dem Laden (fremde
          Projektsperre), stünden die Paare bereits hier. Der Knopf schriebe
          dann `report.mutualHazards`, während der Vermerk in der Koordinierung
          unterbliebe — die Übernahme käme beim nächsten Öffnen ein zweites
          Mal. */}
      {kandidaten.length > 0 && !!onKoordinierungUebernommen && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4">
          <Shuffle className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-blue-900">
              {kandidaten.length} {kandidaten.length === 1 ? 'Paar' : 'Paare'} aus der Koordinierung verfügbar
            </p>
            <p className="text-xs text-blue-800/80 mt-0.5">
              Bestätigte Bewertungen aus der Gewerke-Überschneidung, die hier noch nicht stehen.
              Die Übernahme geschieht nur auf Knopfdruck. Was in einer Zelle schon steht, zeigt der
              Dialog im Wortlaut — überschrieben wird nichts von allein.
            </p>
          </div>
          <Button variant="primary" onClick={oeffneUebernahme} className="shrink-0">
            In die Gefährdungsmatrix übernehmen
          </Button>
        </div>
      )}

      {/* Top Section: Radar (Left) + AI/Score (Right) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        
        {/* Left: Radar (Takes 2/3 space) */}
        <div className="xl:col-span-2">
            <RadarDiagram />
        </div>

        {/* Right: AI & Score (Takes 1/3 space) */}
        <div className="space-y-6">
            <div className="bg-indigo-600 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden group">
                <Sparkles className="absolute top-0 right-0 w-32 h-32 text-white/10 -mr-8 -mt-8 rotate-12 group-hover:rotate-45 transition-transform duration-1000" />
                <h4 className="font-bold text-lg mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    KI-Assistent
                </h4>
                <p className="text-indigo-100 text-xs leading-relaxed mb-6">
                    Lassen Sie die KI mögliche Gefährdungen zwischen den Gewerken analysieren. Wir nutzen dafür die von Ihnen hinterlegten "Tätigkeiten" in der Kontaktliste.
                </p>
                <Button 
                    variant="ai" 
                    onClick={fetchAiSuggestions} 
                    disabled={isAiLoading || trades.length < 2}
                    className="w-full shadow-lg py-3 text-white"
                >
                    {isAiLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                    Konflikte vorhersagen
                </Button>
                {aiMeldung ? (
                    <p role="alert" className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-white">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{aiMeldung}</span>
                    </p>
                ) : aiSuggestions.length > 0 && (
                    // Die Liste steht unter Radar und Risiko-Score, am Handy
                    // außer Sicht: sonst änderte sich nach dem Lauf hier nichts.
                    <p className="mt-3 text-xs leading-relaxed text-indigo-100">
                        {vorschlaegeZeile(aiSuggestions.length)}
                    </p>
                )}
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-slate-400" />
                    Risiko-Score
                </h4>
                <div className="space-y-4">
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-500">Kritische Hotspots</span>
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-bold text-xs">
                            {hazards.filter(h => h.severity === 'high').length}
                        </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div 
                            className="bg-red-500 h-full rounded-full transition-all duration-1000" 
                            style={{ width: `${Math.min(100, (hazards.filter(h => h.severity === 'high').length / 5) * 100)}%` }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Bottom Section: Full Width Matrix */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-[500px]">
          <div className="bg-slate-50 p-6 border-b border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
             <div>
                <h2 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                    <Shuffle className="w-5 h-5 text-blue-600" />
                    Koordinations-Matrix
                </h2>
                <p className="text-xs text-slate-500 mt-1">Gewerke-Interaktion und Präventionsmaßnahmen.</p>
             </div>
             <div className="flex bg-white rounded-xl border border-slate-200 p-1 shadow-sm">
                <button 
                  onClick={() => setViewMode('matrix')} 
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'matrix' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <Grid3X3 className="w-3.5 h-3.5" /> Matrix
                </button>
                <button 
                  onClick={() => setViewMode('list')} 
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'list' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <List className="w-3.5 h-3.5" /> Liste ({hazards.length})
                </button>
                {aiSuggestions.length > 0 && (
                  <button 
                    onClick={() => setViewMode('ai')} 
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'ai' ? 'bg-indigo-600 text-white shadow-md' : 'text-indigo-400 hover:text-indigo-600'}`}
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Vorschläge ({aiSuggestions.length})
                  </button>
                )}
             </div>
          </div>

          <div className="flex-1 overflow-auto">
            {viewMode === 'matrix' ? (
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="bg-slate-50 border-b border-r border-slate-200 p-4 sticky left-0 z-20 w-[150px]"></th>
                    {trades.map(t => (
                      <th key={t} className="bg-slate-50 border-b border-slate-200 p-4 text-[10px] font-black uppercase text-slate-400 tracking-wider text-center min-w-[120px]">
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trades.map(source => (
                    <tr key={source}>
                      <td className="bg-slate-50 border-r border-slate-200 p-4 text-[10px] font-black uppercase text-slate-500 tracking-wider sticky left-0 z-10">
                        {source}
                      </td>
                      {trades.map(affected => {
                        const hazard = getHazard(source, affected);
                        const isSelf = source === affected;
                        const severityColor = hazard?.severity === 'high' ? 'bg-red-500' : hazard?.severity === 'medium' ? 'bg-yellow-400' : 'bg-blue-500';
                        
                        return (
                          <td 
                            key={`${source}-${affected}`}
                            onClick={() => handleCellClick(source, affected)}
                            className={`border border-slate-100 p-4 transition-all cursor-pointer text-center relative
                              ${isSelf ? 'bg-slate-100/50 cursor-not-allowed' : 'hover:bg-blue-50/50'}
                            `}
                          >
                            {hazard ? (
                              <div className="flex items-center justify-center animate-scale-in">
                                 <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform ${severityColor} text-white`}>
                                   <AlertTriangle className="w-4 h-4" />
                                 </div>
                              </div>
                            ) : !isSelf ? (
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-100 mx-auto"></div>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : viewMode === 'list' ? (
              <div className="p-6 space-y-4">
                {hazards.length === 0 ? (
                  <div className="text-center py-20 text-slate-400">
                    <Shuffle className="w-12 h-12 mx-auto mb-4 opacity-10" />
                    Keine Konflikte definiert.
                  </div>
                ) : (
                  hazards.map(h => (
                    <div 
                      key={h.id} 
                      onClick={() => handleCellClick(h.tradeSource, h.tradeAffected)}
                      className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-400 transition-all cursor-pointer shadow-sm hover:shadow-md flex flex-col md:flex-row gap-6 items-start md:items-center"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                           <span className="px-3 py-1 bg-slate-100 rounded-full font-bold text-slate-800 text-xs">{h.tradeSource}</span>
                           <ArrowRight className="w-4 h-4 text-blue-400" />
                           <span className="px-3 py-1 bg-blue-50 rounded-full font-bold text-blue-800 text-xs">{h.tradeAffected}</span>
                        </div>
                        <p className="text-sm text-slate-700 font-medium mb-1 whitespace-pre-line">{h.description}</p>
                        <p className="text-xs text-slate-500 italic flex items-center gap-1 whitespace-pre-line">
                            <ShieldCheck className="w-3 h-3 text-emerald-500"/> {h.measure}
                        </p>
                      </div>
                      <div className="shrink-0 flex items-center gap-4">
                        <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${h.severity === 'high' ? 'bg-red-100 text-red-600' : h.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' : 'bg-blue-100 text-blue-600'}`}>
                          {h.severity}
                        </span>
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
                <div className="p-6 space-y-4 bg-indigo-50/30 min-h-full">
                    <div className="bg-indigo-100 text-indigo-800 p-4 rounded-xl text-xs font-medium flex items-center gap-3 mb-4 border border-indigo-200">
                        <Info className="w-5 h-5 shrink-0" />
                        <span>
                            Diese Gefährdungen wurden von der KI basierend auf Ihren Projektbeteiligten und deren Tätigkeiten identifiziert.
                            {aiHinweis && <span className="block mt-1 font-semibold">{aiHinweis}</span>}
                        </span>
                    </div>
                    {aiSuggestions.map((s, idx) => (
                        <div key={idx} className="bg-white border border-indigo-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all animate-slide-up" style={{animationDelay: `${idx * 50}ms`}}>
                             <div className="flex flex-col md:flex-row justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="font-bold text-slate-800 text-sm">{s.tradeSource}</span>
                                        <ArrowRight className="w-4 h-4 text-indigo-400" />
                                        <span className="font-bold text-indigo-700 text-sm">{s.tradeAffected}</span>
                                        <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${s.severity === 'high' ? 'bg-red-100 text-red-600' : s.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' : 'bg-blue-100 text-blue-600'}`}>
                                            {s.severity}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-800 font-bold mb-1 whitespace-pre-line">{s.description}</p>
                                    <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded-lg border border-slate-100 whitespace-pre-line">{s.measure}</p>
                                </div>
                                <div className="shrink-0">
                                    <Button variant="ai" onClick={() => applyAiSuggestion(s)} className="w-full md:w-auto h-full px-6 py-2 text-white">
                                        Übernehmen
                                    </Button>
                                </div>
                             </div>
                        </div>
                    ))}
                </div>
            )}
          </div>
      </div>

      {/* Übernahme-Dialog: die Namensbrücke wird hier ENTSCHIEDEN, nicht geraten */}
      {uebernahmeOffen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
            <div className="bg-slate-900 text-white p-6 flex justify-between items-start">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Shuffle className="w-5 h-5 text-blue-400" />
                  Übernahme aus der Koordinierung
                </h3>
                <p className="text-[11px] text-slate-300 mt-2 max-w-lg leading-relaxed">
                  Die Koordinierung führt die Gewerke des SiGe-Plans, diese Matrix die Beteiligten des
                  Berichts. Ordnen Sie jedes Gewerk der Zeile zu, unter der es hier stehen soll —
                  sonst stehen zwei Namen für dieselbe Firma in der Matrix.
                </p>
              </div>
              <button onClick={() => setUebernahmeOffen(false)} className="hover:bg-white/10 p-1 rounded transition-colors"><X /></button>
            </div>

            <div className="p-6 space-y-3 overflow-y-auto">
              {vorschlaege.map(v => (
                <div key={v.norm} className="flex flex-wrap items-center gap-3 border-b border-slate-100 pb-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800">{v.label}</p>
                    <p className="text-[11px] text-slate-400">
                      {v.anzahl} {v.anzahl === 1 ? 'Bewertung' : 'Bewertungen'}
                      {v.herkunft === 'taetigkeit' && ' · über die Tätigkeit zugeordnet'}
                      {v.herkunft === 'offen' && (
                        <span className="text-amber-600 font-bold"> · keine eindeutige Zeile gefunden</span>
                      )}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />
                  <select
                    value={zuordnung[v.norm] || ''}
                    onChange={e => setZuordnung(prev => ({ ...prev, [v.norm]: e.target.value }))}
                    className="text-xs font-bold border border-slate-300 rounded-lg px-2 py-1.5 bg-white max-w-full"
                  >
                    <option value="">— nicht übernehmen —</option>
                    {trades.map(t => <option key={t} value={t}>{t}</option>)}
                    {!trades.includes(v.label) && (
                      <option value={v.label}>Neue Zeile: {v.label}</option>
                    )}
                  </select>
                </div>
              ))}

              {/* Belegte Zellen — NAMENTLICH und im Wortlaut.

                  Eine Zahl allein („N ersetzt") sagt niemandem, welchen Satz er
                  gleich verliert. Deshalb steht hier der Alttext, daneben das
                  Neue, und die Wahl liegt bei der Zelle statt beim ganzen
                  Vorgang. */}
              {vorschau.konflikte.length > 0 && (
                <div className="pt-3 space-y-3">
                  <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                    In {vorschau.konflikte.length === 1 ? 'dieser Zelle' : 'diesen Zellen'} steht bereits etwas
                  </p>
                  {vorschau.konflikte.map(k => {
                    const wahl: Konfliktloesung = konfliktwahl[k.schluessel] || KONFLIKT_STANDARD;
                    const wahlen: { id: Konfliktloesung; label: string; hinweis: string }[] = [
                      { id: 'zusammenfuehren', label: 'Zusammenführen', hinweis: 'Beides steht in der Zelle, nichts geht verloren.' },
                      { id: 'ersetzen', label: 'Ersetzen', hinweis: 'Der vorhandene Text wird gelöscht.' },
                      { id: 'behalten', label: 'Nicht übernehmen', hinweis: 'Die Zelle bleibt unverändert; das Paar wird später erneut angeboten.' },
                    ];
                    return (
                      <div key={k.schluessel} className="border border-slate-200 rounded-xl p-3 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-slate-800">{k.source}</span>
                          <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />
                          <span className="text-xs font-bold text-blue-700">{k.affected}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className={`rounded-lg p-2 border ${wahl === 'ersetzen' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                              Steht heute in der Zelle
                              {wahl === 'ersetzen' && <span className="text-red-600"> · wird gelöscht</span>}
                              {wahl === 'behalten' && <span className="text-slate-600"> · bleibt allein stehen</span>}
                            </p>
                            <p className="text-[11px] text-slate-800 whitespace-pre-line break-words">{k.bestand.description || '—'}</p>
                            {k.bestand.measure && (
                              <p className="text-[11px] text-slate-500 whitespace-pre-line break-words mt-1">{k.bestand.measure}</p>
                            )}
                          </div>
                          <div className={`rounded-lg p-2 border ${wahl === 'behalten' ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-blue-200 bg-blue-50'}`}>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-500 mb-1">
                              Aus der Koordinierung
                              {wahl === 'behalten' && <span className="text-slate-500"> · wird nicht übernommen</span>}
                            </p>
                            <p className="text-[11px] text-slate-800 whitespace-pre-line break-words">{k.neu.description || '—'}</p>
                            {k.neu.measure && (
                              <p className="text-[11px] text-slate-500 whitespace-pre-line break-words mt-1">{k.neu.measure}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {wahlen.map(o => (
                            <button
                              key={o.id}
                              type="button"
                              title={o.hinweis}
                              onClick={() => setKonfliktwahl(prev => ({ ...prev, [k.schluessel]: o.id }))}
                              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-colors ${
                                wahl === o.id
                                  ? (o.id === 'ersetzen'
                                    ? 'bg-red-600 border-red-600 text-white'
                                    : 'bg-slate-900 border-slate-900 text-white')
                                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'
                              }`}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 leading-relaxed">
                          {wahlen.find(o => o.id === wahl)?.hinweis}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-6 bg-slate-50 border-t flex flex-wrap justify-between items-center gap-4">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {vorschau.neueZellen} neue {vorschau.neueZellen === 1 ? 'Zelle' : 'Zellen'}
                {vorschau.zusammengefuehrteZellen > 0 && `, ${vorschau.zusammengefuehrteZellen} zusammengeführt`}
                {vorschau.behalteneZellen > 0 && `, ${vorschau.behalteneZellen} unverändert`}
                {vorschau.ersetzteZellen > 0 && (
                  <span className="text-red-600 font-bold">
                    {`, ${vorschau.ersetzteZellen} ersetzt (Text wird gelöscht)`}
                  </span>
                )}
                {vorschau.uebersprungen.length > 0 && (
                  <span className="text-amber-600 font-bold">
                    {' '}· {vorschau.uebersprungen.length} ohne Zuordnung übersprungen
                  </span>
                )}
              </p>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={() => setUebernahmeOffen(false)}>Abbrechen</Button>
                <Button
                  variant="primary"
                  onClick={uebernehmen}
                  disabled={vorschau.uebernommenePaarIds.length === 0}
                  icon={<Save className="w-4 h-4" />}
                >
                  {vorschau.uebernommenePaarIds.length} übernehmen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {editingHazard && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
           <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden animate-scale-in">
              <div className="bg-slate-900 text-white p-6 flex justify-between items-center">
                 <div>
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-400" />
                        Konflikt-Management
                    </h3>
                    <div className="flex items-center gap-2 mt-2">
                        <span className="text-[11px] text-slate-300 font-black uppercase tracking-widest">{editingHazard.source}</span>
                        <ArrowRight className="w-3 h-3 text-blue-400" />
                        <span className="text-[11px] text-blue-300 font-black uppercase tracking-widest">{editingHazard.affected}</span>
                    </div>
                 </div>
                 <button onClick={() => setEditingHazard(null)} className="hover:bg-white/10 p-1 rounded transition-colors"><X /></button>
              </div>

              <div className="p-6 space-y-6">
                 <div>
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-3 text-center">Risikostufe festlegen</label>
                    <div className="flex gap-2">
                       {[
                           {id: 'low', label: 'Gering', color: 'bg-blue-500'},
                           {id: 'medium', label: 'Mittel', color: 'bg-yellow-400'},
                           {id: 'high', label: 'Hoch', color: 'bg-red-500'}
                       ].map(s => (
                         <button 
                            key={s.id} 
                            onClick={() => setFormData({...formData, severity: s.id as any})}
                            className={`flex-1 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all border-2
                                ${formData.severity === s.id 
                                    ? `${s.color} border-transparent text-white shadow-lg scale-105` 
                                    : 'border-slate-100 text-slate-400 hover:border-slate-200 bg-slate-50/50'}
                            `}
                         >
                            {s.label}
                         </button>
                       ))}
                    </div>
                 </div>

                 <div className="space-y-4">
                    <div>
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Gefährdungspotenzial</label>
                        <textarea 
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            className="w-full border border-slate-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            placeholder="z.B. Lärmbelastung durch Stemmarbeiten..."
                        />
                    </div>

                    <div>
                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Koordinations-Maßnahme</label>
                        <textarea 
                            rows={3}
                            value={formData.measure}
                            onChange={(e) => setFormData({...formData, measure: e.target.value})}
                            className="w-full border border-emerald-100 bg-emerald-50/20 rounded-xl p-4 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                            placeholder="z.B. Gehörschutzpflicht oder zeitversetzte Ausführung..."
                        />
                    </div>
                 </div>
              </div>

              <div className="p-6 bg-slate-50 border-t flex justify-between items-center">
                 <button onClick={removeHazard} className="text-red-500 text-[11px] font-black hover:text-red-700 transition-colors uppercase tracking-widest">Entfernen</button>
                 <div className="flex gap-3">
                    <Button variant="ghost" onClick={() => setEditingHazard(null)}>Abbrechen</Button>
                    <Button variant="primary" onClick={() => saveHazard()} icon={<Save className="w-4 h-4"/>}>Speichern</Button>
                 </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};
